import {
  DynamicSchemaDefinition,
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionFileIds,
  DynamicSchemaDefinitionMultiLineText,
  DynamicSchemaDefinitionPositiveInteger,
  DynamicSchemaDefinitionSelectMultiple,
  DynamicSchemaDefinitionSingleLineText,
} from '../../../../generated/graphql';
import {
  DynamicSchemaValue,
  DynamicSchemaValueErrors,
  FileIdsValue,
  MultiLineTextValue,
  PositiveIntegerValue,
  SelectMultipleValue,
  SelectOneValue,
  SingleLineTextValue,
} from '../../../types/DynamicSchema';
import { EditingProjectState, EditingProjectStateErrors } from '../../../types/EditingProjectState';

export const getErrors = (errors: EditingProjectStateErrors): Record<string, Array<Error>> => {
  return {
    creationSets: errors.creationSetsErrors,
    title: errors.titleErrors,
    ...errors.assetCategoryValueEmptyErrors,
    ...errors.creativeBriefValueEmptyErrors,
    ...errors.creativeBriefValueUnacceptableErrors,
  };
};

export const hasAnyErrorsOfMandatoryFieldsExceptForTitle = (errors: EditingProjectStateErrors): boolean => {
  return [...errors.titleErrors, ...errors.creationSetsErrors].length >= 1;
};

const validateCreationSet = (creationSet: EditingProjectState['creationSet']): Array<Error> => {
  const errorBuilder = [];
  if (creationSet === null || creationSet.creativeFormats.length === 0) {
    errorBuilder.push(new Error('creation_sets.no_data'));
  }
  return errorBuilder;
};

const validateTitle = (title: EditingProjectState['title']): Array<Error> => {
  return title.trim().length === 0 || title === 'Untitled Project' ? [new Error('project_title.invalid_value')] : [];
};

const validateAcceptableDynamicSchemaValue = (
  dynamicSchemaValue: Readonly<DynamicSchemaValue> | null,
  dynamicSchemaDefinition?: Readonly<DynamicSchemaDefinition>,
): DynamicSchemaValueErrors => {
  if (dynamicSchemaValue == null || dynamicSchemaDefinition == null) return {};

  const errors: DynamicSchemaValueErrors = {};

  const fields = dynamicSchemaDefinition.sections.reduce(
    (whole, section) => [...whole, ...section.fields],
    [] as Array<DynamicSchemaDefinitionField>,
  );

  fields.forEach((field) => {
    const value = dynamicSchemaValue.values[field.key];
    const errorsOfThisField: Array<Error> = [];

    if (value === undefined) return;

    if (field.type === 'POSITIVE_INTEGER') {
      const typedValue = value as PositiveIntegerValue;

      if (typedValue != null && (!Number.isSafeInteger(typedValue) || typedValue < 0)) {
        errorsOfThisField.push(new Error('positive_integer.invalid_value'));
      }
    }

    if (errorsOfThisField.length >= 1) {
      errors[field.key] = errorsOfThisField;
    }
  });

  return errors;
};

const validateRequiredDynamicSchemaValue = (
  dynamicSchemaValue: Readonly<DynamicSchemaValue> | null,
  dynamicSchemaDefinition?: Readonly<DynamicSchemaDefinition>,
): DynamicSchemaValueErrors => {
  if (dynamicSchemaValue == null || dynamicSchemaDefinition == null) return {};

  const errors: DynamicSchemaValueErrors = {};
  const fields = dynamicSchemaDefinition.sections.reduce(
    (whole, section) => [...whole, ...section.fields],
    [] as Array<DynamicSchemaDefinitionField>,
  );

  fields.forEach((field) => {
    const value = dynamicSchemaValue.values[field.key];
    const errorsOfThisField: Array<Error> = [];

    if (value === undefined) return;

    if (field.type === 'SELECT_MULTIPLE') {
      const typedValue = value as SelectMultipleValue;
      const numberOfCheckedItems = typedValue.filter((v) => !!v).length;

      if ((field as DynamicSchemaDefinitionSelectMultiple).isRequiredToSelectAnything && numberOfCheckedItems === 0) {
        errorsOfThisField.push(new Error('select_multiple.have_to_select_at_least_one'));
      }
    }

    if (field.type === 'SINGLE_LINE_TEXT') {
      const typedValue = value as SingleLineTextValue;

      if ((field as DynamicSchemaDefinitionSingleLineText).isRequired && typedValue.trim().length === 0) {
        errorsOfThisField.push(new Error('single_line_text.invalid_value'));
      }
    }

    if (field.type === 'MULTI_LINE_TEXT') {
      const typedValue = value as MultiLineTextValue;

      if ((field as DynamicSchemaDefinitionMultiLineText).isRequired && typedValue.trim().length === 0) {
        errorsOfThisField.push(new Error('multi_line_text.invalid_value'));
      }
    }

    if (field.type === 'POSITIVE_INTEGER') {
      const typedValue = value as PositiveIntegerValue;

      if (typedValue === null) {
        if ((field as DynamicSchemaDefinitionPositiveInteger).isRequired) {
          errorsOfThisField.push(new Error('positive_integer.invalid_value'));
        }
      }
    }

    if (errorsOfThisField.length >= 1) {
      errors[field.key] = errorsOfThisField;
    }
  });

  return errors;
};

const validateRequiredAssetCategoryValue = (
  assetCategoryValue?: Readonly<DynamicSchemaValue> | null,
  assetCategoryDefinition?: Readonly<DynamicSchemaDefinition> | null,
) => {
  if (assetCategoryValue == null || assetCategoryDefinition == null) return {};

  const errors: DynamicSchemaValueErrors = {};

  const _makeErrorMessage = (
    hasSingleLineTextField: boolean,
    isYourOwnChoice: boolean,
    isNoFieldId: boolean,
    isNoUrl: boolean,
    isRequired: boolean,
    mainField: DynamicSchemaDefinitionFileIds,
  ): void => {
    if (hasSingleLineTextField) {
      if (isYourOwnChoice && isNoFieldId && isNoUrl) {
        errors[mainField.key] = [new Error('creative_brief_editor.field.validation_error.your_own_choice')];
      }
      if (isRequired && isNoFieldId && isNoUrl) {
        errors[mainField.key] = [new Error('creative_brief_editor.field.validation_error.has_no_file_ids_value')];
      }
    } else {
      if (isYourOwnChoice && isNoFieldId) {
        errors[mainField.key] = [new Error('creative_brief_editor.field.validation_error.your_own_choice')];
      }
    }
  };

  assetCategoryDefinition.sections.forEach((section) => {
    const { meta } = section;
    if (meta == null) return;
    if (meta.isRequired == null) return;
    if (meta.isRequired === false && meta.isAllowLeaveToTheCreators === false) return;

    const mainField = section.fields.find((field) => field.type === 'FILE_IDS') as DynamicSchemaDefinitionFileIds;
    let isYourOwnChoice = false;
    let hasSingleLineTextField = false;
    let isNoFieldId = false;
    let isNoUrl = false;

    section.fields.forEach((field) => {
      const value = assetCategoryValue.values[field.key];

      if (field.type === 'SELECT_ONE' && meta.isAllowLeaveToTheCreators) {
        if ((value as SelectOneValue) !== 'unnecessary' && (value as SelectOneValue) !== 'leave_to_the_creators') {
          isYourOwnChoice = true;
        }
      }

      if (field.type === 'SINGLE_LINE_TEXT') {
        hasSingleLineTextField = true;
      }

      if (field.type === 'SINGLE_LINE_TEXT' && (value as SingleLineTextValue) === '') {
        isNoUrl = true;
      }

      if (field.type === 'FILE_IDS' && (value as FileIdsValue).length <= 0) {
        isNoFieldId = true;
      }
    });

    _makeErrorMessage(hasSingleLineTextField, isYourOwnChoice, isNoFieldId, isNoUrl, meta.isRequired, mainField);
  });

  return errors;
};

export const validateState = (state: EditingProjectState): EditingProjectStateErrors => {
  return {
    creationSetsErrors: validateCreationSet(state.creationSet),
    titleErrors: validateTitle(state.title),
    creativeBriefValueUnacceptableErrors: validateAcceptableDynamicSchemaValue(
      state.creativeBriefValue,
      state.creativeBriefValue?.definition,
    ),
    creativeBriefValueEmptyErrors: validateRequiredDynamicSchemaValue(
      state.creativeBriefValue,
      state.creativeBriefValue?.definition,
    ),
    assetCategoryValueEmptyErrors: validateRequiredAssetCategoryValue(
      state.assetCategoryValue,
      state.assetCategoryValue?.definition,
    ),
  };
};

export const hasAnyErrors = (errors: EditingProjectStateErrors) => {
  return Object.values(getErrors(errors)).some((error) => error.length >= 1);
};
